import { Link } from 'react-router-dom';
import { ProjectContactForm } from './ProjectContactForm';
import { Phone, Building2 } from 'lucide-react';
import { WhatsAppIcon } from '@/components/property/ContactIcons';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ReportButton } from '@/components/ReportButton';

interface MarketedBy {
  name: string;
  logoUrl?: string | null;
  slug?: string | null;
}

interface ProjectContactSidebarProps {
  projectId: string;
  projectName?: string;
  referenceCode?: string;
  phone?: string;
  whatsapp?: string;
  marketedBy?: MarketedBy | null;
  assignedAgentId?: string | null;
}

export function ProjectContactSidebar({
  projectId,
  projectName,
  referenceCode,
  phone = '+251',
  whatsapp = '251',
  marketedBy,
  assignedAgentId,
}: ProjectContactSidebarProps) {
  const cleanWa = (whatsapp || '').replace(/\D/g, '');
  return (
    <div className="hidden lg:block space-y-4">
      <div className="border rounded-xl overflow-hidden">
        {/* Agent header: photo, name, phone */}
        <div className="p-4 border-b bg-muted/30">
          <Link
            to={marketedBy?.slug ? `/agencies/${marketedBy.slug}` : '#'}
            className="flex items-center gap-3 group"
          >
            <div className="h-14 w-14 rounded-full border-2 border-primary/20 bg-card overflow-hidden flex items-center justify-center shrink-0">
              {marketedBy?.logoUrl ? (
                <img src={marketedBy.logoUrl} alt={marketedBy.name} className="h-full w-full object-cover" />
              ) : (
                <Building2 className="h-6 w-6 text-muted-foreground" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-semibold text-primary uppercase tracking-wide">Marketed by</div>
              <div className="text-sm font-bold truncate group-hover:text-primary transition-colors">
                {marketedBy?.name || 'Project Agent'}
              </div>
              {phone && phone !== '+251' && (
                <a
                  href={`tel:${phone}`}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 text-xs text-primary font-semibold hover:underline mt-0.5"
                >
                  <Phone className="h-3 w-3" /> {phone}
                </a>
              )}
            </div>
          </Link>
        </div>

        <div className="flex">
          <Button className="flex-1 rounded-none h-12 text-sm font-bold gap-2" asChild>
            <a href={`tel:${phone}`}>
              <Phone className="h-4 w-4" /> Call Now
            </a>
          </Button>
          <Button variant="outline" className="rounded-none h-12 w-14 border-l-0" asChild>
            <a
              href={`https://wa.me/${cleanWa}?text=${encodeURIComponent(`Hi, I'd like to inquire about ${projectName || 'this project'}${referenceCode ? ` (${referenceCode.toUpperCase()})` : ''}. Please share availability, pricing and next steps.`)}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp"
            >
              <WhatsAppIcon className="h-4 w-4" />
            </a>
          </Button>
        </div>

        <div className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <Separator className="flex-1" />
            <span className="text-xs text-muted-foreground">or</span>
            <Separator className="flex-1" />
          </div>

          <ProjectContactForm
            projectId={projectId}
            projectName={projectName}
            referenceCode={referenceCode}
            assignedAgentId={assignedAgentId}
          />
        </div>
      </div>

      <div className="border rounded-xl p-3 bg-muted/40">
        <ReportButton
          targetType="project"
          targetId={projectId}
          variant="outline"
          className="w-full border-destructive/30 text-destructive hover:bg-destructive hover:text-destructive-foreground font-semibold text-sm"
          label="Report this project"
        />
      </div>
    </div>
  );
}
